import type { LucideIcon } from 'lucide-react'
import {
  BarChart3, BriefcaseBusiness, FileText, Flag, Gauge, Hash, Layers3, Megaphone,
  Headphones, MessagesSquare, Radio, Scale, ScrollText, Settings2, ShieldCheck, UserCog, Users,
} from 'lucide-react'
import type { StaffPermissions } from '@/types'

export type NavSection = 'Workspace' | 'Moderation' | 'Community' | 'Publishing' | 'Administration'

export interface NavItem {
  id: string
  label: string
  shortLabel?: string
  path: string
  icon: LucideIcon
  section: NavSection
  description?: string
  permission?: (permissions: StaffPermissions) => boolean
}

export const NAV_SECTIONS: NavSection[] = ['Workspace', 'Moderation', 'Community', 'Publishing', 'Administration']

// Keep navigation task-based. Secondary routes such as /voice and /themes
// remain available inside their parent screens instead of becoming duplicates.
export const NAV_ITEMS: NavItem[] = [
  { id: 'overview', label: 'Overview', path: '/', icon: Gauge, section: 'Workspace', description: 'Queues, health, and platform activity' },
  { id: 'reports', label: 'Review queue', path: '/trust-safety/reports', icon: Flag, section: 'Moderation', description: 'Reported content requiring review', permission: (p) => !!p.viewReports },
  { id: 'appeals', label: 'Appeals & privacy', path: '/trust-safety/appeals', icon: Scale, section: 'Moderation', description: 'Appeals and deletion requests', permission: (p) => !!p.viewReports },
  { id: 'cases', label: 'Cases', path: '/trust-safety/cases', icon: ShieldCheck, section: 'Moderation', description: 'Grouped investigations', permission: (p) => !!p.viewReports },
  { id: 'users', label: 'People', path: '/users', icon: Users, section: 'Moderation', description: 'Accounts, restrictions, and history', permission: (p) => !!p.viewReports },
  { id: 'support', label: 'Support desk', path: '/support', icon: Headphones, section: 'Moderation', description: 'Escalations and support workflow', permission: (p) => !!p.manageAnnouncements },
  { id: 'communities', label: 'Communities', path: '/communities', icon: Radio, section: 'Community', description: 'Community directory and ownership', permission: (p) => !!p.manageUsers },
  { id: 'channels', label: 'Channels', path: '/channels', icon: Hash, section: 'Community', description: 'Platform channel directory', permission: (p) => !!p.manageUsers },
  { id: 'messages', label: 'Reported messages', shortLabel: 'Messages', path: '/messages', icon: MessagesSquare, section: 'Community', description: 'User-disclosed message evidence', permission: (p) => !!p.manageReports },
  { id: 'announcements', label: 'Announcements', path: '/announcements', icon: Megaphone, section: 'Publishing', description: 'Platform-wide notices', permission: (p) => !!p.manageAnnouncements },
  { id: 'blog', label: 'Blog', path: '/blog', icon: FileText, section: 'Publishing', description: 'Public updates and release posts', permission: (p) => !!p.manageBlog },
  { id: 'careers-postings', label: 'Job listings', path: '/careers/postings', icon: BriefcaseBusiness, section: 'Publishing', description: 'Open roles and application forms', permission: (p) => !!p.careersAdmin },
  { id: 'careers-applications', label: 'Applications', path: '/careers/applications', icon: FileText, section: 'Publishing', description: 'Candidate review queue', permission: (p) => !!p.careersAdmin },
  { id: 'infrastructure', label: 'Platform health', path: '/infrastructure', icon: Layers3, section: 'Administration', description: 'Services, voice, encryption, and status', permission: (p) => !!p.manageStatus },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: BarChart3, section: 'Administration', description: 'Growth and moderation activity', permission: (p) => !!p.viewAnalytics },
  { id: 'staff', label: 'Staff access', path: '/staff', icon: UserCog, section: 'Administration', description: 'Roles and permissions', permission: (p) => !!p.manageStaff },
  { id: 'audit', label: 'Audit log', path: '/audit', icon: ScrollText, section: 'Administration', description: 'Administrative action history', permission: (p) => !!p.viewAudit },
  { id: 'settings', label: 'Configuration', path: '/settings', icon: Settings2, section: 'Administration', description: 'Safety, themes, and roadmap', permission: (p) => !!p.platformControls || !!p.manageThemes || !!p.roadmapAdmin },
]

export function filterNav(items: NavItem[], permissions: StaffPermissions | null): NavItem[] {
  if (!permissions) return []
  return items.filter((item) => !item.permission || item.permission(permissions))
}

export function navItemForPath(pathname: string, permissions?: StaffPermissions | null): NavItem | undefined {
  const visible = permissions ? filterNav(NAV_ITEMS, permissions) : NAV_ITEMS
  const aliases: Record<string, string> = {
    '/trust-safety/moderation': '/trust-safety/reports',
    '/voice': '/infrastructure',
    '/status': '/infrastructure',
    '/roadmap': '/settings',
    '/themes': '/settings',
    '/safety': '/settings',
  }
  const resolvedPath = aliases[pathname] || pathname
  return visible
    .filter((item) => item.path === '/' ? resolvedPath === '/' : resolvedPath.startsWith(item.path))
    .sort((a, b) => b.path.length - a.path.length)[0]
}

export const ROLE_LABELS: Record<string, string> = {
  developer: 'Developer', mod: 'Moderator', admin: 'T&S Admin', head_ts: 'Head of Trust & Safety',
  executive: 'Executive', owner: 'Owner', administrator: 'Administrator', moderator: 'Moderator',
  trust_safety: 'Trust & Safety', support: 'Support', hr: 'HR', staff: 'Staff',
}
